import type { KeyboardEvent, ReactNode } from 'react'
import { cn } from '@/shared/ui/cn'

export type RecordMobileCardField = {
  label: string
  value: ReactNode
}

export type RecordMobileCardProps = {
  title: ReactNode
  subtitle?: ReactNode
  fields: RecordMobileCardField[]
  rowId?: string
  isExpanded?: boolean
  onClick?: () => void
  children?: ReactNode
}

type CardInteractionProps = {
  role?: 'button'
  tabIndex?: number
  onClick?: () => void
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void
}

export function RecordMobileCard({
  title,
  subtitle,
  fields,
  rowId,
  isExpanded,
  onClick,
  children,
}: RecordMobileCardProps) {
  const interactionProps: CardInteractionProps = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onClick()
          }
        },
      }
    : {}

  return (
    <div
      data-row-id={rowId}
      aria-expanded={isExpanded}
      {...interactionProps}
      className={cn(
        'w-full rounded-lg border bg-card p-3 text-left',
        onClick && 'cursor-pointer transition-colors hover:bg-muted/50',
      )}
    >
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium">{title}</span>
        {subtitle ? (
          <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
        ) : null}
      </div>
      <dl className="mt-2 flex flex-col gap-1">
        {fields.map((field) => (
          <div
            key={field.label}
            className="flex items-baseline justify-between gap-4 text-sm"
          >
            <dt className="text-muted-foreground">{field.label}</dt>
            <dd className="text-right font-mono">{field.value}</dd>
          </div>
        ))}
      </dl>
      {children}
    </div>
  )
}
